class ProjectsFilter extends HTMLElement {
  constructor() {
    super();
    this.controller = new AbortController();
    this.filterTimeout = null;
  }

  connectedCallback() {
    this.blocks = [...this.querySelectorAll('[data-block-category]')];
    this.filterLinks = [...this.querySelectorAll('[data-filter-category]')];
    this.categoriesWithoutHero = (this.dataset.categoriesWithoutHero || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    // Save original class names so "View All" can restore them
    this.originalClasses = new Map();
    this.blocks.forEach((block) => {
      this.originalClasses.set(block, block.className);
    });

    // Prepare blocks for transitions
    this.blocks.forEach((block) => {
      block.style.transition = 'opacity 0.3s ease-in-out';
    });

    // Category link clicks
    this.addEventListener(
      'click',
      (e) => {
        const link = e.target.closest('[data-filter-category]');
        if (!link) return;
        e.preventDefault();
        this.applyFilter(link.dataset.filterCategory, true);
      },
      { signal: this.controller.signal }
    );

    // Browser back/forward
    window.addEventListener(
      'popstate',
      () => {
        const cat =
          new URLSearchParams(window.location.search).get('cat') || '';
        this.applyFilter(cat, true, false);
      },
      { signal: this.controller.signal }
    );

    // Apply filter from URL on initial load (no animation)
    const initialCat =
      new URLSearchParams(window.location.search).get('cat') || '';
    this.applyFilter(initialCat, false);
  }

  disconnectedCallback() {
    clearTimeout(this.filterTimeout);
    this.controller.abort();
  }

  applyFilter(category, animate, updateUrl = true) {
    // Update URL
    if (updateUrl) {
      const url = new URL(window.location);
      if (category) {
        url.searchParams.set('cat', category);
      } else {
        url.searchParams.delete('cat');
      }
      if (url.toString() !== window.location.toString()) {
        history.pushState({}, '', url);
      }
    }

    // Update active link
    this.filterLinks.forEach((link) => {
      link.classList.toggle(
        'underline',
        link.dataset.filterCategory === category
      );
    });

    if (animate) {
      // Fade out all blocks
      this.blocks.forEach((block) => (block.style.opacity = '0'));

      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.showBlocks(category);
        // Fade in visible blocks on next frame
        requestAnimationFrame(() => {
          this.blocks.forEach((block) => {
            if (block.style.display !== 'none') {
              block.style.opacity = '1';
            }
          });
        });
      }, 300);
    } else {
      this.showBlocks(category);
      // Immediately show without transition
      this.blocks.forEach((block) => {
        if (block.style.display !== 'none') {
          block.style.opacity = '1';
        }
      });
    }
  }

  showBlocks(category) {
    this.blocks.forEach((block) => {
      const matches = !category || block.dataset.blockCategory === category;
      block.style.display = matches ? '' : 'none';
      block.style.opacity = '0';
    });
    this.updateLayout(category);
  }

  updateLayout(category) {
    // keep sync with liquid file
    const GRID_CLASSES = [
      'col-[1/13]',
      'md:col-[1/25]',
      'md:-mx-side',
      '-mx-side-small',
      'col-[1/9]',
      'md:col-[17/25]',
      'col-[5/13]',
      'md:col-[5/13]',
    ];

    // "View All": restore original server-rendered classes
    if (!category) {
      this.blocks.forEach((block) => {
        block.className = this.originalClasses.get(block);
      });
      return;
    }

    // Filtered: recalculate grid positions for visible blocks
    const visible = this.blocks.filter((b) => b.style.display !== 'none');
    const heroDisabled = this.categoriesWithoutHero.includes(category);

    visible.forEach((block, i) => {
      block.classList.remove(...GRID_CLASSES);

      const h2 = block.querySelector('h2');
      const catDiv = block.querySelector('.text-small');
      h2?.classList.remove('text-center');
      catDiv?.classList.remove('text-center');

      // First block gets hero treatment if hero is enabled for this category
      if (!heroDisabled && i === 0) {
        block.classList.add(
          'col-[1/13]',
          'md:col-[1/25]',
          'md:-mx-side',
          '-mx-side-small'
        );
        h2?.classList.add('text-center');
        catDiv?.classList.add('text-center');
      } else {
        // Alternating left/right layout
        // With hero: index 1=left, 2=right, 3=left...
        // Without hero: index 0=left, 1=right, 2=left...
        const isLeft = heroDisabled ? i % 2 === 0 : i % 2 === 1;

        if (isLeft) {
          block.classList.add('col-[5/13]', 'md:col-[5/13]');
        } else {
          block.classList.add('col-[1/9]', 'md:col-[17/25]');
        }
      }
    });
  }
}

if (!customElements.get('projects-filter')) {
  customElements.define('projects-filter', ProjectsFilter);
}
